import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

const BADGE_AMBER =
  'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
const BADGE_BLUE =
  'border-blue-400 bg-blue-50 text-blue-800 dark:border-blue-500 dark:bg-blue-900 dark:text-blue-100'
const BADGE_GREEN =
  'border-green-500 bg-green-50 text-green-800 dark:border-green-500 dark:bg-green-900 dark:text-green-100'

export type StandardDataStatusBadgeProps = {
  /** Hazards, information-for-use and/or RRMs present (extraction or manual). */
  hasData: boolean
  /** PDF uploaded (drives blue vs amber when !hasData). */
  hasPdf: boolean
  /**
   * Compliance: only show amber/blue when a user standard is linked; hide when
   * no link. When true, green “data available” is never shown (library-only).
   */
  requireLinkedUserStandard?: boolean
  /** Required when requireLinkedUserStandard is true. */
  userStandardId?: string | null
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

/**
 * Hazards/IFU data status: green when data exists (library), amber (no PDF) or
 * blue (PDF, not extracted) when not. Compliance call sites set
 * requireLinkedUserStandard + userStandardId so the badge only appears for
 * linked standards and never shows green.
 */
export function StandardDataStatusBadge({
  hasData,
  hasPdf,
  requireLinkedUserStandard = false,
  userStandardId = null,
  tooltipSide = 'bottom',
  className,
}: StandardDataStatusBadgeProps) {
  const { t } = useTranslation('standardsLibrary')

  if (requireLinkedUserStandard && !userStandardId) {
    return null
  }
  if (requireLinkedUserStandard && hasData) {
    return null
  }

  if (hasData) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant='outline'
            className={cn('shrink-0', BADGE_GREEN, className)}
          >
            <CheckCircle2 className='mr-1 size-3' />
            {t('detail.dataAvailable')}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide} className='max-w-xs'>
          {t('detail.dataAvailableHint')}
        </TooltipContent>
      </Tooltip>
    )
  }

  const showNotYetExtracted = hasPdf
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant='outline'
          className={cn(
            'shrink-0',
            showNotYetExtracted ? BADGE_BLUE : BADGE_AMBER,
            className
          )}
        >
          <AlertTriangle className='mr-1 size-3' />
          {showNotYetExtracted
            ? t('detail.notYetExtracted')
            : t('detail.noDataYet')}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide} className='max-w-xs'>
        {showNotYetExtracted
          ? t('detail.notYetExtractedHint')
          : t('detail.noDataYetHint')}
      </TooltipContent>
    </Tooltip>
  )
}
